import React, {
  useState,
  useEffect,
  useRef,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import logger from "../utils/logger";

interface OtpInputProps {
  value: string;
  length?: number;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  className?: string;
  disabled?: boolean;
}

export const OTPInput: React.FC<OtpInputProps> = ({
  value,
  length = 6,
  onChange,
  autoFocus = true,
  disabled = false,
  className,
}) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(""));
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  // Reset OTP if parent value is cleared
  useEffect(() => {
    if (value === "") {
      setOtp(Array(length).fill(""));
    }
  }, [value, length]);

  // Handle change in each input
  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const val = e.target.value;

    if (!/^\d?$/.test(val)) return; // Only allow single digit numbers

    const newOtp = [...otp];
    newOtp[index] = val;

    setOtp(newOtp);
    onChange(newOtp.join(""));

    // Move focus to next input
    if (val && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace key
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace") {
      if (otp[index] === "" && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
        onChange(newOtp.join(""));
      }
    }
  };

  // Handle paste event
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").trim().split("");

    if (pasted.length !== length) return;

    const newOtp = pasted.map((char) => (/^\d$/.test(char) ? char : ""));
    setOtp(newOtp);
    onChange(newOtp.join(""));

    const filledIndex = newOtp.findIndex((item) => item === "");
    const focusIndex = filledIndex === -1 ? length - 1 : filledIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  // Handle SMS auto-detect (Web OTP API)
  useEffect(() => {
    if ("OTPCredential" in window) {
      (async () => {
        try {
          const content: any = await (navigator as any).credentials.get({
            otp: { transport: ["sms"] },
          });
          if (content?.code) {
            const autoOtp = content.code.split("");
            setOtp(autoOtp);
            onChange(autoOtp.join(""));
          }
        } catch (err: any) {
          if (err.name !== "AbortError") {
            logger.warn("OTP Auto-fill error:", err);
          }
        }
      })();
    }
  }, [onChange]);

  return (
    <div
      onPaste={handlePaste}
      className={`${className} flex justify-center space-x-2`}
    >
      {otp.map((digit, index) => (
        <input
          key={index}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          autoFocus={autoFocus && index === 0}
          onChange={(e) => handleChange(e, index)}
          onFocus={(e) => e.target.select()}
          onKeyDown={(e) => handleKeyDown(e, index)}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          autoComplete="one-time-code"
          className={`text-inherit w-12 h-12 bg-transparent  text-center text-xl font-semibold  border border-gray-300 rounded-lg  focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all`}
          disabled={disabled}
        />
      ))}
    </div>
  );
};



